import React from 'react';
import { Edit2, Trash2, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { type Lesson, type Word } from '../../types';

interface LessonCardProps {
  lesson: Lesson;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEditLesson: () => void;
  onDeleteLesson: () => void;
  onAddWord: () => void;
  onEditWord: (word: Word) => void;
  onDeleteWord: (wordId: string) => void;
}

export function LessonCard({
  lesson,
  isExpanded,
  onToggleExpand,
  onEditLesson,
  onDeleteLesson,
  onAddWord,
  onEditWord,
  onDeleteWord
}: LessonCardProps) {
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="p-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center">
              <button
                onClick={onToggleExpand}
                className="mr-2 text-gray-500 hover:text-gray-700"
              >
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </button>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{lesson.title}</h3>
                <p className="mt-1 text-sm text-gray-500">{lesson.description}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center space-x-4">
              <span className="text-sm text-gray-500">Level: {lesson.level}</span>
              <span className="text-sm text-gray-500">Category: {lesson.category}</span>
              <span className={`text-sm px-2 py-1 rounded ${
                lesson.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                lesson.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {lesson.difficulty}
              </span>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onEditLesson}
              className="p-2 text-indigo-600 hover:text-indigo-900"
            >
              <Edit2 className="h-5 w-5" />
            </button>
            <button
              onClick={onDeleteLesson}
              className="p-2 text-red-600 hover:text-red-900"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isExpanded && (
          <>
            {lesson.text && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900">Lesson Text:</h4>
                <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{lesson.text}</p>
              </div>
            )}

            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-900">Words:</h4>
                <button
                  onClick={onAddWord}
                  className="flex items-center text-sm text-indigo-600 hover:text-indigo-900"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Word
                </button>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {lesson.words?.map((word) => (
                  <div key={word.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h5 className="text-sm font-medium text-gray-900">{word.dutch}</h5>
                        <p className="text-sm text-gray-500">{word.translation}</p>
                        {word.pronunciation && (
                          <p className="text-xs text-gray-400">/{word.pronunciation}/</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => onEditWord(word)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDeleteWord(word.id!)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {word.example && (
                      <div className="mt-2 text-sm">
                        <p className="text-gray-600">{word.example}</p>
                        <p className="text-gray-500 italic">{word.example_translation}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}